import { NavigateFunction } from "react-router-dom";
import { toast } from "react-toastify";
import i18n from "@/i18n";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import Paths from "@/constants/paths";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { DeliveryStatusEnum } from "@/constants/enums/deliveryStatus.enum";
import { resolveDeliveryStatusGroup } from "@/components/shared/notificationItem";

export type Row = GetAllExpeditionBoResponseDtoExpedition;

export const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

export const getInitials = (person: { firstName: string; lastName: string }): string =>
  person.firstName.slice(0, 1).toUpperCase() + person.lastName.slice(0, 1).toUpperCase();

export const getExpeditionRef = (row: Row): string =>
  row.quotation ? "C-" + row.quotation.identifier : "D-" + row.customQuotation?.identifier;

const DHL_TNT_TRACKING_URL_PATTERNS = ["mydhl.", "tnt."];

export const isDhlTntTracking = (tracking: Row["tracking"] | undefined): boolean => {
  if (!tracking?.manualTracking || !tracking.trackingUrl) return false;
  const trackingUrl = tracking.trackingUrl.toLowerCase();
  return DHL_TNT_TRACKING_URL_PATTERNS.some((pattern) => trackingUrl.includes(pattern));
};

export const resolveExpeditionRowTarget = (row: Row): { pathname: string; search: string } => {
  const processStatus = row.processStatus;
  const deliveryStatus = row.deliveryStatus;
  const isDeleted = row.deletedAt;
  const isQuotation = row.quotation !== null;
  const identifier = row.quotation?.identifier || row.customQuotation?.identifier;

  if (!identifier) return { pathname: "", search: "" };

  // Prefixed with C-/D- and flagged `exact` so the destination page matches this one
  // expedition exactly, instead of falling back to the fuzzy free-text search (which
  // would also match other identifiers sharing the same digits, or unrelated client names).
  const exactSearch = `search=${isQuotation ? "C" : "D"}-${identifier}&exact=true`;

  if (processStatus === ExpeditionProcessStatusEnum.DELETED || isDeleted) {
    return {
      pathname: Paths.bo.deletedExpeditionsV2,
      search: exactSearch,
    };
  }
  if (
    !isQuotation &&
    (processStatus === ExpeditionProcessStatusEnum.PENDING_QUOTE ||
      processStatus === ExpeditionProcessStatusEnum.PENDING_QUOTE_ACCEPTATION)
  ) {
    return { pathname: Paths.bo.customQuotationsBox, search: exactSearch };
  }
  if (deliveryStatus === DeliveryStatusEnum.BLOCKED) {
    return { pathname: Paths.bo.blockedExpeditionsV2, search: exactSearch };
  }
  if (processStatus === ExpeditionProcessStatusEnum.PENDING_PELICAN) {
    return { pathname: Paths.bo.expeditionsBox, search: `${exactSearch}&week=false` };
  }
  if (processStatus === ExpeditionProcessStatusEnum.VALIDATED) {
    return {
      pathname: Paths.bo.validatedExpeditionsV2,
      search: `${exactSearch}&group=${resolveDeliveryStatusGroup(deliveryStatus)}`,
    };
  }
  return { pathname: "", search: "" };
};

export const handleExpeditionRowClick = (row: Row, navigate: NavigateFunction) => {
  const { pathname, search } = resolveExpeditionRowTarget(row);
  if (pathname) {
    navigate({ pathname, search });
    return;
  }
  if (row.processStatus === ExpeditionProcessStatusEnum.PENDING_CUSTOMER) {
    toast.info(i18n.t("boExpeditions:expeditionRow.notFinalizedByClient"));
  }
};

export const expeditionRowClassname = (row: Row, heightClass?: string): string => {
  const prefix = heightClass ? `${heightClass} ` : "";
  if (row.deletedRequestAt) return `${prefix}bg-red-50 hover:bg-red-100/70`;
  if (row.basedOnId !== null) return `${prefix}bg-blue-50 hover:bg-blue-100/70`;
  if (row.notification && row.notification.length > 0 && row.notification[0].readById === null) {
    return `${prefix}bg-yellow-50 hover:bg-yellow-100/70`;
  }
  return `${prefix}bg-white`;
};
